"use client"

import { useRef, useMemo, useState, useEffect } from "react"
import { Canvas, useFrame } from "@react-three/fiber"
import { OrbitControls, Trail, Html } from "@react-three/drei"
import * as THREE from "three"
import { useTheme } from "./theme-provider"

// Task color mapping with more vibrant colors
const TASK_COLORS = {
  memory: "#0088ff", // Vibrant Blue
  "decision making": "#00dd55", // Vibrant Green
  attention: "#ffdd00", // Vibrant Yellow
  vision: "#aa00ff", // Vibrant Purple
  balance: "#ff7700", // Vibrant Orange
  speech: "#00dddd", // Vibrant Teal
  emotion: "#ff0077", // Vibrant Pink
  movement: "#77ff00", // Vibrant Lime
  hearing: "#dd00ff", // Vibrant Magenta
  default: "#aaaaaa", // Default gray
}

// Map brain activities to tasks for color coding
const ACTIVITY_TO_TASK = {
  memory: "memory",
  "memory formation": "memory",
  "spatial memory": "memory",
  "long-term memory": "memory",
  "visual memory": "memory",
  "emotional memory": "memory",
  "memory consolidation": "memory",

  "decision making": "decision making",
  planning: "decision making",
  "problem solving": "decision making",
  judgment: "decision making",
  "executive function": "decision making",

  attention: "attention",
  focus: "attention",
  concentration: "attention",

  vision: "vision",
  "visual processing": "vision",
  "color recognition": "vision",
  "motion perception": "vision",
  "visual interpretation": "vision",
  "depth perception": "vision",

  balance: "balance",
  coordination: "balance",
  "motor control": "balance",
  posture: "balance",

  speech: "speech",
  language: "speech",
  "speech comprehension": "speech",

  emotion: "emotion",
  "emotional processing": "emotion",
  fear: "emotion",
  anxiety: "emotion",
  aggression: "emotion",

  movement: "movement",
  "fine movement": "movement",
  "muscle coordination": "movement",

  hearing: "hearing",
  "auditory processing": "hearing",
}

// Brain regions and their primary tasks
const REGION_TO_TASKS = {
  "frontal lobe": ["decision making", "attention", "speech", "movement"],
  "temporal lobe": ["memory", "hearing", "speech", "emotion"],
  "parietal lobe": ["attention", "movement", "balance"],
  "occipital lobe": ["vision"],
  cerebellum: ["balance", "coordination", "movement"],
  brainstem: ["balance", "movement"],
  hippocampus: ["memory"],
  amygdala: ["emotion"],
}

// Enhanced descriptions for neural signals
const SIGNAL_DESCRIPTIONS = {
  memory: "Memory formation and retrieval pathway",
  "decision making": "Executive function and reasoning pathway",
  attention: "Attentional control and focus pathway",
  vision: "Visual processing and interpretation pathway",
  balance: "Balance and motor coordination pathway",
  speech: "Language and speech production pathway",
  emotion: "Emotional processing and regulation pathway",
  movement: "Motor control and coordination pathway",
  hearing: "Auditory processing pathway",
  default: "General neural pathway",
}

// Function to get neurotransmitter names based on task
const getTaskNeurotransmitters = (task) => {
  const neurotransmitters = {
    memory: ["Acetylcholine", "Glutamate"],
    "decision making": ["Dopamine", "Norepinephrine"],
    attention: ["Dopamine", "Norepinephrine"],
    vision: ["Glutamate", "GABA"],
    balance: ["GABA", "Glycine"],
    speech: ["Glutamate", "Acetylcholine"],
    emotion: ["Serotonin", "Dopamine"],
    movement: ["Dopamine", "Acetylcholine"],
    hearing: ["Glutamate", "Glycine"],
    default: ["Glutamate", "GABA"],
  }

  return neurotransmitters[task] || neurotransmitters.default
}

// Neural pathway label component
function PathwayLabel({ position, text, color, theme }) {
  return (
    <Html position={position} center>
      <div
        style={{
          backgroundColor: theme === "dark" ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)",
          color: color,
          padding: "6px 12px", // Larger padding
          borderRadius: "8px",
          fontSize: "14px", // Larger font size
          fontWeight: "bold",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          border: `2px solid ${color}`, // Thicker border
          boxShadow: "0 3px 8px rgba(0,0,0,0.2)", // Stronger shadow
        }}
      >
        {text}
      </div>
    </Html>
  )
}

// Neurotransmitter label component
function NeurotransmitterLabel({ position, name, change, theme }) {
  const changeColor = change === "increasing" ? "#28a745" : "#dc3545"
  const arrow = change === "increasing" ? "↑" : "↓"

  return (
    <Html position={position} center>
      <div
        style={{
          backgroundColor: theme === "dark" ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)",
          color: theme === "dark" ? "#e0e0e0" : "#333",
          padding: "6px 12px", // Larger padding
          borderRadius: "12px",
          fontSize: "14px", // Larger font size
          fontWeight: "bold",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          border: `2px dashed ${changeColor}`, // Colored border
          boxShadow: "0 3px 8px rgba(0,0,0,0.2)", // Stronger shadow
        }}
      >
        {name} <span style={{ color: changeColor, fontSize: "16px" }}>{arrow}</span>
      </div>
    </Html>
  )
}

// Neuron tooltip component
function NeuronTooltip({ position, task, active, theme }) {
  return (
    <Html position={position} center distanceFactor={15}>
      <div
        style={{
          background: theme === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.7)",
          color: theme === "dark" ? "#e0e0e0" : "white",
          padding: "5px 10px",
          borderRadius: "4px",
          fontSize: "11px",
          pointerEvents: "none",
          transform: "translate3d(-50%, -100%, 0)",
          marginTop: "-15px",
          opacity: active ? 1 : 0,
          transition: "opacity 0.3s",
          display: active ? "block" : "none",
        }}
      >
        <div>
          <strong>{task.charAt(0).toUpperCase() + task.slice(1)}</strong>
        </div>
        <div>Neurotransmitters: {getTaskNeurotransmitters(task).join(", ")}</div>
      </div>
    </Html>
  )
}

// Signal particle system for neural impulses
function SignalParticle({ path, color, speed, delay = 0, looping = true, task, theme }) {
  const ref = useRef()
  const [active, setActive] = useState(false)
  const [progress, setProgress] = useState(0)
  // Reduce label frequency to avoid clutter
  const [showLabel, setShowLabel] = useState(false)

  // Start animation after delay
  useEffect(() => {
    const timer = setTimeout(() => setActive(true), delay)

    // Show labels for only 10% of signals to reduce clutter
    setShowLabel(Math.random() > 0.9)

    return () => clearTimeout(timer)
  }, [delay])

  useFrame(({ clock }) => {
    if (!active || !ref.current || !path) return

    // Calculate position along the path
    const time = clock.getElapsedTime() * speed - delay / 1000
    let newProgress = time % 1

    // If not looping, stop at end of path
    if (!looping && newProgress >= 1) {
      newProgress = 1
    }

    setProgress(newProgress)

    try {
      // Position the particle along the path
      const point = path.getPointAt(newProgress)
      if (point) {
        ref.current.position.copy(point)

        // Get direction tangent for orientation
        if (newProgress < 0.99) {
          const tangent = path.getTangentAt(newProgress)
          if (tangent && tangent.length() > 0) {
            const lookAt = new THREE.Vector3().copy(point).add(tangent)
            ref.current.lookAt(lookAt)
          }
        }
      }
    } catch (error) {
      // Silently handle any curve-related errors
      console.debug("Curve error:", error)
    }
  })

  // Get midpoint along the path for label placement
  const labelPosition = useMemo(() => {
    if (!path) return [0, 0, 0]
    try {
      const point = path.getPointAt(0.5)
      return point ? [point.x, point.y + 0.2, point.z] : [0, 0, 0]
    } catch (error) {
      return [0, 0, 0]
    }
  }, [path])

  return (
    <group ref={ref}>
      <Trail width={1.5} length={10} color={color} attenuation={(t) => t * t} decay={2}>
        <mesh>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshBasicMaterial color={color} />
        </mesh>
      </Trail>

      {/* Show label for few signals to avoid clutter */}
      {showLabel && task && (
        <PathwayLabel position={labelPosition} text={SIGNAL_DESCRIPTIONS[task]} color={color} theme={theme} />
      )}
    </group>
  )
}

// Neuron with dynamic action potential visualization
function Neuron({
  position,
  color,
  size = 0.15,
  pulsing = false,
  firing = false,
  task = "default",
  showTooltip = false,
  theme,
}) {
  const ref = useRef()
  const [firingState, setFiringState] = useState(0)
  const [hover, setHover] = useState(false)

  // Simulate action potential
  useFrame(({ clock }) => {
    if (ref.current) {
      // Basic pulsing animation
      if (pulsing) {
        ref.current.scale.setScalar(1 + Math.sin(clock.getElapsedTime() * 3) * 0.2)
      }

      // Action potential visualization (depolarization and repolarization)
      if (firing) {
        // Cycle through firing states
        const cycleTime = clock.getElapsedTime() % 5

        if (cycleTime < 0.2) {
          // Rapid depolarization
          setFiringState(1)
        } else if (cycleTime < 0.4) {
          // Peak
          setFiringState(2)
        } else if (cycleTime < 0.6) {
          // Repolarization
          setFiringState(3)
        } else if (cycleTime < 1.0) {
          // Hyperpolarization
          setFiringState(4)
        } else {
          // Resting state
          setFiringState(0)
        }
      }
    }
  })

  // Determine neuron appearance based on firing state
  const getNeuronColor = () => {
    if (!firing) return color

    switch (firingState) {
      case 1:
        return "#ffffff" // Depolarization - bright white
      case 2:
        return "#ffff00" // Peak - yellow
      case 3:
        return color // Repolarization - back to normal
      case 4:
        return theme === "dark" ? "#333333" : "#444444" // Hyperpolarization - darker
      default:
        return color // Resting state
    }
  }

  const getEmissiveIntensity = () => {
    if (!firing) return theme === "dark" ? 0.7 : 0.5
    return firingState === 1 || firingState === 2 ? 1.5 : 0.3
  }

  return (
    <group>
      <mesh position={position} ref={ref} onPointerOver={() => setHover(true)} onPointerOut={() => setHover(false)}>
        <sphereGeometry args={[size, 16, 16]} />
        <meshStandardMaterial
          color={getNeuronColor()}
          emissive={getNeuronColor()}
          emissiveIntensity={getEmissiveIntensity()}
          roughness={0.3}
          metalness={0.2}
        />
      </mesh>

      {/* Show tooltips when hovered or explicitly specified */}
      {(hover || showTooltip) && task && (
        <NeuronTooltip
          position={[position[0], position[1] + 0.3, position[2]]}
          task={task}
          active={true}
          theme={theme}
        />
      )}
    </group>
  )
}

// Dendrite and axon connection with signal flow visualization
function Connection({ start, end, color, thickness = 0.02, active = false, task, theme }) {
  // Calculate a fixed midPoint based on start and end
  const midPoint = useMemo(
    () => [
      (start[0] + end[0]) / 2 + (Math.random() - 0.5) * 0.5,
      (start[1] + end[1]) / 2 + (Math.random() - 0.5) * 0.5,
      (start[2] + end[2]) / 2 + (Math.random() - 0.5) * 0.5,
    ],
    [start, end],
  )

  // Create the curve safely with error checking
  const [curve, tubeGeometry] = useMemo(() => {
    try {
      const bezierCurve = new THREE.QuadraticBezierCurve3(
        new THREE.Vector3(...start),
        new THREE.Vector3(...midPoint),
        new THREE.Vector3(...end),
      )

      // Generate points - validate the curve before creating geometry
      const points = bezierCurve.getPoints(20)
      if (points.length < 2) {
        throw new Error("Not enough points for curve")
      }

      // Create a smooth curve through the points
      const catmullRomCurve = new THREE.CatmullRomCurve3(points)

      // Create the tube geometry
      const tube = new THREE.TubeGeometry(catmullRomCurve, 20, thickness, 8, false)

      return [catmullRomCurve, tube]
    } catch (error) {
      console.debug("Error creating curve:", error)
      return [null, null]
    }
  }, [start, end, midPoint, thickness])

  // Synaptic terminals at the end of the connection
  const [synapticTerminals] = useState(() => {
    const terminals = []
    const terminalCount = Math.floor(Math.random() * 3) + 1

    for (let i = 0; i < terminalCount; i++) {
      const offset = new THREE.Vector3(
        (Math.random() - 0.5) * 0.1,
        (Math.random() - 0.5) * 0.1,
        (Math.random() - 0.5) * 0.1,
      )
      terminals.push(offset)
    }

    return terminals
  })

  // If no valid curve, don't render anything
  if (!curve || !tubeGeometry) {
    return null
  }

  return (
    <>
      {/* The axon/dendrite */}
      <mesh geometry={tubeGeometry}>
        <meshBasicMaterial color={color} transparent opacity={theme === "dark" ? 0.8 : 0.7} />
      </mesh>

      {/* Synaptic terminals */}
      {synapticTerminals.map((offset, i) => (
        <mesh key={i} position={[end[0] + offset.x, end[1] + offset.y, end[2] + offset.z]}>
          <sphereGeometry args={[thickness * 2, 8, 8]} />
          <meshStandardMaterial color={color} emissive={color} emissiveIntensity={theme === "dark" ? 0.5 : 0.3} />
        </mesh>
      ))}

      {/* Animate signals along active connections */}
      {active && curve && (
        <>
          <SignalParticle
            path={curve}
            color={color}
            speed={0.5}
            delay={Math.random() * 2000}
            task={task}
            theme={theme}
          />
          {Math.random() > 0.5 && (
            <SignalParticle
              path={curve}
              color={color}
              speed={0.5}
              delay={Math.random() * 2000 + 2000}
              task={task}
              theme={theme}
            />
          )}
        </>
      )}
    </>
  )
}

// Neurotransmitter visualization at synapses
function Neurotransmitter({ position, color, task, theme }) {
  const ref = useRef()
  const [active, setActive] = useState(false)
  const [hover, setHover] = useState(false)

  // Random movement
  useFrame(() => {
    if (ref.current && active) {
      ref.current.position.x += (Math.random() - 0.5) * 0.01
      ref.current.position.y += (Math.random() - 0.5) * 0.01
      ref.current.position.z += (Math.random() - 0.5) * 0.01
    }
  })

  useEffect(() => {
    // Start moving after delay
    const timer = setTimeout(() => setActive(true), Math.random() * 1000)
    return () => clearTimeout(timer)
  }, [])

  // Get neurotransmitter name based on task
  const neurotransmitters = getTaskNeurotransmitters(task)
  const neurotransmitter = neurotransmitters[Math.floor(Math.random() * neurotransmitters.length)]

  return (
    <group>
      <mesh ref={ref} position={position} onPointerOver={() => setHover(true)} onPointerOut={() => setHover(false)}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.8} />
      </mesh>

      {/* Tooltip on hover */}
      {hover && (
        <Html position={[position[0], position[1] + 0.1, position[2]]} center>
          <div
            style={{
              background: theme === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.7)",
              color: theme === "dark" ? "#e0e0e0" : "white",
              padding: "3px 6px",
              borderRadius: "3px",
              fontSize: "10px",
              pointerEvents: "none",
            }}
          >
            {neurotransmitter}
          </div>
        </Html>
      )}
    </group>
  )
}

// Mood transition visualization component
function MoodTransitionPanel({ moodTransition, theme }) {
  if (!moodTransition) return null

  return (
    <Html position={[0, 2.5, 0]} center>
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          backgroundColor: theme === "dark" ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)",
          padding: "12px 16px", // Larger padding
          borderRadius: "10px",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)", // Stronger shadow
          zIndex: 10,
          maxWidth: "250px", // Wider panel
          borderLeft: "4px solid " + (theme === "dark" ? "#5c7cfa" : "#4361ee"),
          fontSize: "14px", // Larger font size
          color: theme === "dark" ? "#e0e0e0" : "#333",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "16px" }}>Mood Transition</div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "8px 0",
            fontWeight: "bold",
          }}
        >
          <span style={{ color: "#28a745", fontSize: "16px" }}>{moodTransition.from.toUpperCase()}</span>
          <span style={{ margin: "0 8px", color: theme === "dark" ? "#909090" : "#666" }}>→</span>
          <span style={{ color: "#dc3545", fontSize: "16px" }}>{moodTransition.to.toUpperCase()}</span>
        </div>
        <div style={{ marginTop: "8px" }}>
          {moodTransition.neurotransmitters.map((nt, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: nt.change === "increasing" ? "#28a745" : "#dc3545",
                marginBottom: "4px",
                fontWeight: "bold",
              }}
            >
              <span>{nt.name}</span>
              <span style={{ fontSize: "16px" }}>{nt.change === "increasing" ? "↑" : "↓"}</span>
            </div>
          ))}
        </div>
      </div>
    </Html>
  )
}

function NeuralNetworkModel({ activity, moodTransition, theme }) {
  // Determine the task based on the activity
  const task = activity
    ? ACTIVITY_TO_TASK[activity.toLowerCase()] ||
      Object.keys(REGION_TO_TASKS).find(
        (region) =>
          region.toLowerCase().includes(activity.toLowerCase()) ||
          activity.toLowerCase().includes(region.toLowerCase()),
      ) ||
      "default"
    : "default"

  // Generate neurons in a 3D network structure
  const neurons = useMemo(() => {
    const result = []
    const layerCount = 4
    const neuronsPerLayer = [8, 12, 10, 6]

    // Create neurons for each layer in 3D space
    let x = -3
    for (let layer = 0; layer < layerCount; layer++) {
      const count = neuronsPerLayer[layer]

      // Calculate neurons in a spherical arrangement for each layer
      for (let i = 0; i < count; i++) {
        // Calculate position on a sphere for a more 3D arrangement
        const phi = Math.acos(-1 + (2 * i) / count)
        const theta = Math.sqrt(count * Math.PI) * phi

        // Adjust radius for each layer to create depth
        const radius = 1.5

        // Convert spherical to cartesian coordinates
        const y = radius * Math.sin(phi) * Math.cos(theta)
        const z = radius * Math.sin(phi) * Math.sin(theta)

        // Assign a task to each neuron
        let neuronTask
        if (layer === 0) {
          // Input layer - assign different tasks
          neuronTask = Object.keys(TASK_COLORS).filter((t) => t !== "default")[
            i % (Object.keys(TASK_COLORS).length - 1)
          ]
        } else if (layer === layerCount - 1) {
          // Output layer - assign to brain regions
          neuronTask = Object.keys(REGION_TO_TASKS)[i % Object.keys(REGION_TO_TASKS).length]
        } else {
          // Hidden layers - mix of tasks
          neuronTask = Object.keys(TASK_COLORS).filter((t) => t !== "default")[
            (i + layer) % (Object.keys(TASK_COLORS).length - 1)
          ]
        }

        // Determine if this neuron should be highlighted based on activity
        const active =
          task === neuronTask || (neuronTask in REGION_TO_TASKS && REGION_TO_TASKS[neuronTask].includes(task))

        // Show tooltips for key neurons in the network
        const showTooltip = (layer === 0 || layer === layerCount - 1) && neuronTask === task && i % 3 === 0

        result.push({
          position: [x, y, z],
          layer,
          task: neuronTask,
          id: `neuron-${layer}-${i}`,
          active,
          // Add firing state property - neurons occasionally fire spontaneously, active neurons fire more frequently
          firing: Math.random() > (active ? 0.3 : 0.8),
          // Show tooltips for some neurons
          showTooltip,
        })
      }
      x += 2 // Move to next layer along x-axis
    }

    return result
  }, [task])

  // Generate connections between neurons in 3D
  const connections = useMemo(() => {
    const result = []
    const layerCount = 4
    const neuronsPerLayer = [8, 12, 10, 6]

    // Connect each layer to the next
    let neuronIndex = 0
    for (let layer = 0; layer < layerCount - 1; layer++) {
      const currentLayerStart = neuronIndex
      const nextLayerStart = currentLayerStart + neuronsPerLayer[layer]

      for (let i = 0; i < neuronsPerLayer[layer]; i++) {
        const from = neurons[currentLayerStart + i]

        // Connect to some neurons in the next layer
        for (let j = 0; j < neuronsPerLayer[layer + 1]; j++) {
          // Create more connections for active neurons, but keep density reasonable
          if (Math.random() > 0.7 || (from.active && Math.random() > 0.3)) {
            const to = neurons[nextLayerStart + j]

            // Calculate if connection is active based on source neuron's firing state
            const connActive = from.active && from.firing && (Math.random() > 0.5 || task === from.task)

            result.push({
              start: from.position,
              end: to.position,
              sourceId: from.id,
              targetId: to.id,
              task: from.task,
              active: connActive,
            })
          }
        }
      }

      neuronIndex += neuronsPerLayer[layer]
    }

    return result
  }, [neurons, task])

  // Create neurotransmitters at synapses
  const neurotransmitters = useMemo(() => {
    const result = []

    // Only add neurotransmitters to active connections
    connections
      .filter((conn) => conn.active)
      .forEach((connection, i) => {
        const particleCount = Math.floor(Math.random() * 5) + 3

        for (let j = 0; j < particleCount; j++) {
          // Create particles near the end of the connection (synapse)
          const endPoint = new THREE.Vector3(...connection.end)
          const offset = new THREE.Vector3(
            (Math.random() - 0.5) * 0.15,
            (Math.random() - 0.5) * 0.15,
            (Math.random() - 0.5) * 0.15,
          )

          const position = endPoint.add(offset)

          result.push({
            position: [position.x, position.y, position.z],
            color: TASK_COLORS[connection.task] || TASK_COLORS.default,
            task: connection.task,
            id: `neurotransmitter-${i}-${j}`,
          })
        }
      })

    return result
  }, [connections])

  // Create neurotransmitter labels for mood transitions
  const neurotransmitterLabels = useMemo(() => {
    if (!moodTransition || !moodTransition.neurotransmitters) return []

    const result = []

    // Create a label for each neurotransmitter in the mood transition
    moodTransition.neurotransmitters.forEach((nt, i) => {
      // Position labels at different locations in the scene
      const x = -2 + i * 1.5
      const y = 1.5
      const z = -1

      result.push({
        position: [x, y, z],
        name: nt.name,
        change: nt.change,
      })
    })

    return result
  }, [moodTransition])

  return (
    <>
      {/* Render connections first (behind neurons) */}
      {connections.map((connection, i) => (
        <Connection
          key={`connection-${i}`}
          start={connection.start}
          end={connection.end}
          color={connection.active ? TASK_COLORS[task] : theme === "dark" ? "#555555" : "#aaaaaa"}
          thickness={connection.active ? 0.03 : 0.01}
          active={connection.active}
          task={connection.task}
          theme={theme}
        />
      ))}

      {/* Render neurons */}
      {neurons.map((neuron, i) => (
        <Neuron
          key={`neuron-${i}`}
          position={neuron.position}
          color={neuron.active ? TASK_COLORS[task] : TASK_COLORS[neuron.task] || TASK_COLORS.default}
          size={neuron.active ? 0.2 : 0.15}
          pulsing={neuron.active}
          firing={neuron.firing}
          task={neuron.task}
          showTooltip={neuron.showTooltip}
          theme={theme}
        />
      ))}

      {/* Render neurotransmitters */}
      {neurotransmitters.map((nt, i) => (
        <Neurotransmitter
          key={`neurotransmitter-${i}`}
          position={nt.position}
          color={nt.color}
          task={nt.task}
          theme={theme}
        />
      ))}

      {/* Render neurotransmitter labels for mood transitions */}
      {neurotransmitterLabels.map((label, i) => (
        <NeurotransmitterLabel
          key={`nt-label-${i}`}
          position={label.position}
          name={label.name}
          change={label.change}
          theme={theme}
        />
      ))}

      {/* Render mood transition panel if available */}
      {moodTransition && <MoodTransitionPanel moodTransition={moodTransition} theme={theme} />}
    </>
  )
}

// Neural Process Legend
function NeuralProcessLegend({ task, theme }) {
  return (
    <Html position={[0, 2.5, 0]} center>
      <div
        style={{
          background: theme === "dark" ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.9)",
          padding: "8px 12px",
          borderRadius: "8px",
          border: `2px solid ${TASK_COLORS[task] || TASK_COLORS.default}`,
          fontSize: "12px",
          pointerEvents: "none",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          minWidth: "200px",
          maxWidth: "300px",
          color: theme === "dark" ? "#e0e0e0" : "#333",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "5px",
            color: TASK_COLORS[task] || (theme === "dark" ? "#e0e0e0" : "#333"),
          }}
        >
          Active Process: {task.charAt(0).toUpperCase() + task.slice(1)}
        </div>
        <div style={{ marginBottom: "5px" }}>
          <span style={{ fontWeight: "bold" }}>Neurotransmitters:</span> {getTaskNeurotransmitters(task).join(", ")}
        </div>
        <div style={{ fontSize: "11px", color: theme === "dark" ? "#b0b0b0" : "#555" }}>
          {SIGNAL_DESCRIPTIONS[task]}
        </div>
      </div>
    </Html>
  )
}

// Rotating group with information panel
function RotatingGroup({ activity, moodTransition, theme }) {
  const groupRef = useRef()

  // Now useFrame is safely inside a component rendered within Canvas
  useFrame(({ clock }) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(clock.getElapsedTime() * 0.2) * 0.3
    }
  })

  // Determine task for legend display
  const task = activity ? ACTIVITY_TO_TASK[activity.toLowerCase()] || "default" : "default"

  return (
    <group ref={groupRef}>
      <NeuralNetworkModel activity={activity} moodTransition={moodTransition} theme={theme} />
      {task !== "default" && !moodTransition && <NeuralProcessLegend task={task} theme={theme} />}
    </group>
  )
}

// Enhanced information panel for neural network simulation details
function InfoPanel({ activity, moodTransition, theme }) {
  const task = activity ? ACTIVITY_TO_TASK[activity.toLowerCase()] || "default" : "default"

  const infoText = moodTransition
    ? `Simulating ${moodTransition.from} to ${moodTransition.to} transition`
    : activity
      ? `Simulating ${activity} activity in neural network`
      : "Idle neural network state"

  return (
    <div
      style={{
        position: "absolute",
        bottom: "20px",
        left: "20px",
        background: theme === "dark" ? "rgba(0,0,0,0.8)" : "rgba(0,0,0,0.8)",
        color: "white",
        padding: "12px 16px", // Larger padding
        borderRadius: "8px",
        fontSize: "14px", // Larger font size
        pointerEvents: "none",
        minWidth: "250px", // Wider panel
        maxWidth: "350px",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)", // Stronger shadow
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "6px", fontSize: "16px" }}>{infoText}</div>

      {moodTransition ? (
        <div style={{ marginTop: "6px", fontSize: "14px" }}>
          <strong>Transition:</strong> {moodTransition.from} → {moodTransition.to}
          <br />
          <strong>Key Neurotransmitters:</strong>
          <ul style={{ margin: "6px 0", paddingLeft: "20px" }}>
            {moodTransition.neurotransmitters.map((nt, i) => (
              <li key={i}>
                {nt.name}:{" "}
                <span style={{ color: nt.change === "increasing" ? "#8aff8a" : "#ff8a8a", fontWeight: "bold" }}>
                  {nt.change === "increasing" ? "↑" : "↓"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        task !== "default" && (
          <div style={{ marginTop: "6px", fontSize: "14px" }}>
            <strong>Active Process:</strong> {task}
            <br />
            <strong>Neurotransmitters:</strong> {getTaskNeurotransmitters(task).join(", ")}
          </div>
        )
      )}

      <div
        style={{
          marginTop: "8px",
          fontSize: "12px",
          opacity: 0.8,
        }}
      >
        Hover over neurons and signals for more information
      </div>
    </div>
  )
}

export default function NeuralNetwork({ activity, moodTransition }) {
  const { theme } = useTheme()

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
        <color attach="background" args={[theme === "dark" ? "#1a1a2e" : "#f8f9fa"]} />
        <ambientLight intensity={theme === "dark" ? 0.6 : 0.7} />
        <pointLight position={[10, 10, 10]} intensity={theme === "dark" ? 1.0 : 1.2} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />
        <RotatingGroup activity={activity} moodTransition={moodTransition} theme={theme} />
        <OrbitControls enableZoom={true} enablePan={true} />
      </Canvas>
      
      {/* Brain Orientation Labels */}
      <div style={{
        position: "absolute",
        top: "10px",
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.7)",
        color: "#fff",
        padding: "8px 16px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: "600",
        pointerEvents: "none",
        zIndex: 10
      }}>
        ⬆️ Front (Frontal Lobe)
      </div>
      
      <div style={{
        position: "absolute",
        bottom: "60px",
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.7)",
        color: "#fff",
        padding: "8px 16px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: "600",
        pointerEvents: "none",
        zIndex: 10
      }}>
        ⬇️ Back (Occipital Lobe)
      </div>
      
      <InfoPanel activity={activity} moodTransition={moodTransition} theme={theme} />
    </div>
  )
}
